import { ReactNode, useMemo } from "react";
import { useAuthContext } from "providers/Auth";
import { SectionCard } from "shared/app/SectionCard";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { Select, Show, TextArea } from "shared/components";
import type { Option } from "shared/components/forms/Select";
import { BaseInput } from "shared/components/forms/BaseInput";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { type Register } from "shared/hooks";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { PropertyKind } from "shared/types";
import { PmsAddressValue } from "types/landlord/address";
import { states } from "utils/stateCodes";
import { propertyTypeOptions } from "../AddProperty/constants";
import {
  ADDRESS_LABEL,
  formatPmsAddress,
  pmsManualTooltip,
  pmsSyncedTooltip
} from "../UnitGeneralForm/constants";

interface GeneralPmsValues {
  name?: string;
  address?: PmsAddressValue;
}

interface GeneralPmsOverrides {
  name?: boolean;
  address?: boolean;
}

const READ_ONLY_INPUT_CLASS = "[&_.bg-white]:!bg-alpha-40";
const DISABLED_TEXTAREA_CLASS = "bg-alpha-40 text-content-100";
const TOOLTIP_CLASS =
  "max-w-[320px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white";

const isSyncedWithPms = (
  overrides: GeneralPmsOverrides | undefined,
  key: keyof GeneralPmsOverrides
) => overrides?.[key] !== false;

interface PMSPropertyGeneralFormProps {
  register: Register;
  values: Record<string, unknown>;
  isDefaultProperty?: boolean;
  headerExtra?: ReactNode;
  footer?: ReactNode;
  extraSections?: ReactNode;
}

export const PMSPropertyGeneralForm = ({
  register,
  values,
  isDefaultProperty,
  headerExtra,
  footer,
  extraSections
}: PMSPropertyGeneralFormProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { type } = useAuthContext();

  const pmsValues = values.pms_values as GeneralPmsValues | undefined;
  const overrides = values.pms_overrides as GeneralPmsOverrides | undefined;

  const nameSynced = isSyncedWithPms(overrides, "name");
  const addressSynced = isSyncedWithPms(overrides, "address");

  const nameLabel = `${dictionary.Property} name`;
  const kindOptions = useMemo<Option[]>(
    () =>
      propertyTypeOptions.map((opt) =>
        opt.value === PropertyKind.ManufacturedHousing
          ? { ...opt, label: "Mobile Home" }
          : opt
      ) as Option[],
    []
  );

  const setOverride = (key: keyof GeneralPmsOverrides, value: boolean) => {
    register("pms_overrides").onChange({ ...(overrides || {}), [key]: value });
  };

  const handleNameSync = (value: boolean) => {
    setOverride("name", value);
    if (value && pmsValues?.name) register("name").onChange(pmsValues.name);
  };

  const handleAddressSync = (value: boolean) => {
    setOverride("address", value);

    const pmsAddress = pmsValues?.address;
    if (!value || !pmsAddress?.address1) return;

    register("address1").onChange(pmsAddress.address1);
    register("address2").onChange(pmsAddress.address2 ?? "");
    register("city").onChange(pmsAddress.city ?? "");
    register("state_or_province").onChange(pmsAddress.state_or_province ?? "");
    register("postal_code").onChange(pmsAddress.postal_code ?? "");
  };

  return (
    <SectionCard title="General" headerExtra={headerExtra} footer={footer}>
      <div className="divide-y divide-alpha-40">
        <div className="grid grid-cols-2 gap-4 items-center pb-4">
          <div>
            <Tooltip>
              <TooltipTrigger className="w-full text-left">
                <BaseInput
                  id="property-name"
                  label={nameLabel}
                  placeholder={nameSynced ? "-" : "Lemon Grove Apartments"}
                  required
                  readOnly={nameSynced}
                  className={nameSynced ? READ_ONLY_INPUT_CLASS : ""}
                  value={register("name").value ?? ""}
                  onChange={(val) => register("name").onChange(val)}
                  error={register("name").error}
                />
              </TooltipTrigger>
              <TooltipContent className={TOOLTIP_CLASS}>
                <p>
                  {nameSynced
                    ? pmsSyncedTooltip(nameLabel)
                    : pmsManualTooltip(nameLabel)}
                </p>
              </TooltipContent>
            </Tooltip>
            <PmsSyncOption
              name="property-name-sync"
              value={nameSynced}
              onChange={handleNameSync}
            />
          </div>
          <PMSValue value={pmsValues?.name} />
        </div>

        {/* The PMS never sends a property type, so it is always editable. */}
        <Show if={type.isBoomCRM}>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Select
                id="property-type"
                label={`${dictionary.Property} type`}
                required
                defaultValue={propertyTypeOptions[0].value ?? undefined}
                options={kindOptions}
                value={register("kind").value ?? undefined}
                onChange={(val) => register("kind").onChange(val)}
                error={register("kind").error}
              />
            </div>
          </div>
        </Show>

        <div className="py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 items-center">
            <div>
              <Tooltip>
                <TooltipTrigger className="w-full text-left">
                  <p className="text-base font-medium text-content-1000">
                    {ADDRESS_LABEL}
                  </p>
                </TooltipTrigger>
                <TooltipContent className={TOOLTIP_CLASS}>
                  <p>
                    {addressSynced
                      ? pmsSyncedTooltip(ADDRESS_LABEL)
                      : pmsManualTooltip(ADDRESS_LABEL)}
                  </p>
                </TooltipContent>
              </Tooltip>
              <PmsSyncOption
                name="property-address-sync"
                value={addressSynced}
                onChange={handleAddressSync}
              />
            </div>
            <PMSValue value={formatPmsAddress(pmsValues?.address)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="w-full">
              <BaseInput
                id="selected-place"
                label="Street address"
                placeholder="2972 Westheimer Rd"
                required
                readOnly={addressSynced}
                className={addressSynced ? READ_ONLY_INPUT_CLASS : ""}
                value={register("address1").value ?? ""}
                onChange={(val) => register("address1").onChange(val)}
                error={register("address1").error}
              />
            </div>
            <div className="w-full">
              <BaseInput
                id="address2"
                label="Apartment, suite, etc."
                placeholder="Apartment 12"
                readOnly={addressSynced}
                className={addressSynced ? READ_ONLY_INPUT_CLASS : ""}
                value={register("address2").value ?? ""}
                onChange={(val) => register("address2").onChange(val)}
                error={register("address2").error}
              />
            </div>
            <div className="w-full">
              <BaseInput
                id="city"
                label="City"
                placeholder="Austin"
                required
                readOnly={addressSynced}
                className={addressSynced ? READ_ONLY_INPUT_CLASS : ""}
                value={register("city").value ?? ""}
                onChange={(val) => register("city").onChange(val)}
                error={register("city").error}
              />
            </div>
            <div className="flex w-full justify-end gap-4">
              <div className="mb-auto h-fit flex-1">
                <Select
                  id="state-or-province"
                  label="State"
                  placeholder="State"
                  searchable
                  hideOptional
                  options={states}
                  isDisabled={addressSynced}
                  value={register("state_or_province").value ?? undefined}
                  onChange={(val) =>
                    register("state_or_province").onChange(val)
                  }
                  error={register("state_or_province").error}
                  showErrorMessage
                />
              </div>
              <div className="flex-1">
                <BaseInput
                  id="postal-code"
                  label="Zip"
                  required
                  placeholder="11111"
                  readOnly={addressSynced}
                  className={addressSynced ? READ_ONLY_INPUT_CLASS : ""}
                  value={register("postal_code").value ?? ""}
                  onChange={(val) => register("postal_code").onChange(val)}
                  error={register("postal_code").error}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Description and Property ID are rewritten by every sync and have no
            override of their own yet, so they stay read-only here. */}
        {isDefaultProperty ? null : (
          <div className="py-4">
            <TextArea
              id="property-description"
              label="Description"
              placeholder="-"
              rows={5}
              disabled
              className={DISABLED_TEXTAREA_CLASS}
              value={register("description").value ?? ""}
              onChange={(val) => register("description").onChange(val)}
              error={register("description").error}
            />
          </div>
        )}

        <div className="pt-4 grid grid-cols-2 gap-4">
          <div className="w-full">
            <BaseInput
              id="property-id"
              label={`${dictionary.Property} ID`}
              placeholder="-"
              readOnly
              className={READ_ONLY_INPUT_CLASS}
              value={register("external_id").value ?? ""}
              onChange={(val) => register("external_id").onChange(val)}
              error={register("external_id").error}
            />
          </div>
        </div>

        {extraSections}
      </div>
    </SectionCard>
  );
};
